import type { PointerEvent } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { parBand, pointLabel, type GridCell, type PlacementStatus } from '../../services/lightSurveyService';
import './Lighting.css';

export interface ParMapPoint {
  readonly id: string;
  readonly sequence: number;
  readonly x: number;
  readonly y: number;
  /** The value to show (simulated when the intensity is simulated). */
  readonly par: number;
}

export interface ParMapCoral {
  readonly id: string;
  /** 1-based number, the same as in the coral list. */
  readonly number: number;
  readonly x: number;
  readonly y: number;
  readonly status: PlacementStatus;
}

export interface ParMapPhotoProps {
  imageUrl: string | null;
  /** Width / height of the photo — used for the frame when there is no photo. */
  aspect: number;
  points: readonly ParMapPoint[];
  corals: readonly ParMapCoral[];
  grid: readonly GridCell[];
  showGrid: boolean;
  /** Measured points, corals and the estimated area; false = the plain photo (the pending ring stays). */
  showMeasurements?: boolean;
  /** Points to emphasise ("where can a new coral go?"); null = no emphasis. */
  highlightIds: ReadonlySet<string> | null;
  /** The position picked for the form (0–1), drawn as a ring. */
  pending: { x: number; y: number } | null;
  /** Tap / click on the photo → position for the form. Keyboard users type the position in the form. */
  onPick?: (x: number, y: number) => void;
}

const pct = (value: number): string => `${Math.max(0, Math.min(100, value * 100))}%`;
const round = (value: number): number => Math.round(Math.max(0, Math.min(1, value)) * 1000) / 1000;

const CORAL_GLYPH: Readonly<Record<PlacementStatus, string>> = {
  ok: '✓',
  too_low: '!',
  too_high: '!',
  uncertain: '?',
  unknown_group: '?',
  not_applicable: '',
  no_data: '?',
};

/**
 * The survey photo with the PAR overlay: measured points (letter + value), the
 * estimated area near them, and the placed corals (number + status glyph). The
 * overlay is decorative (aria-hidden): the point and coral lists below are the
 * accessible UI, and the position fields of the form are the keyboard way to
 * place something — a tap on the photo only fills them in.
 */
export function ParMapPhoto({ imageUrl, aspect, points, corals, grid, showGrid, showMeasurements = true, highlightIds, pending, onPick }: ParMapPhotoProps) {
  const { t } = useTranslation();
  const pick = (event: PointerEvent<HTMLDivElement>): void => {
    if (!onPick || event.button > 0) return;
    const rect = event.currentTarget.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    onPick(round((event.clientX - rect.left) / rect.width), round((event.clientY - rect.top) / rect.height));
  };
  const frameClass = ['par-map__frame', onPick ? '' : 'par-map__frame--readonly', imageUrl ? '' : 'par-map__frame--no-photo'].filter(Boolean).join(' ');
  return (
    <figure className="par-map">
      <div
        className={frameClass}
        data-testid="par-map-frame"
        onPointerUp={pick}
        style={imageUrl ? undefined : { aspectRatio: String(aspect) }}
      >
        {imageUrl && <img className="par-map__photo" src={imageUrl} alt={t('lighting.map.photoAlt')} draggable={false} />}
        <div className="par-map__layer" aria-hidden="true">
          {showMeasurements &&
            showGrid &&
            grid.map((cell) => (
              <span
                key={`${cell.x}-${cell.y}`}
                className={`par-map__cell par-map__cell--${parBand(cell.par)}`}
                data-testid="par-map-cell"
                style={{ left: pct(cell.x), top: pct(cell.y), width: pct(cell.width), height: pct(cell.height) }}
              />
            ))}
          {showMeasurements && points.map((p) => {
            const emphasis = highlightIds ? (highlightIds.has(p.id) ? ' par-map__point--highlight' : ' par-map__point--dimmed') : '';
            return (
              <span
                key={p.id}
                className={`par-map__point par-map__point--${parBand(p.par)}${emphasis}`}
                data-testid="par-map-point"
                style={{ left: pct(p.x), top: pct(p.y) }}
              >
                <span className="par-map__point-label">{pointLabel(p.sequence)}</span>
                {p.par}
              </span>
            );
          })}
          {showMeasurements && corals.map((c) => (
            <span key={c.id} className={`par-map__coral par-map__coral--${c.status}`} data-testid="par-map-coral" style={{ left: pct(c.x), top: pct(c.y) }}>
              {c.number}
              {CORAL_GLYPH[c.status]}
            </span>
          ))}
          {pending && <span className="par-map__pending" data-testid="par-map-pending" style={{ left: pct(pending.x), top: pct(pending.y) }} />}
        </div>
      </div>
    </figure>
  );
}

/** Legend of the light scale: swatch + range in words and numbers. */
export function ParMapLegend() {
  const { t } = useTranslation();
  return (
    <ul className="par-map__legend" aria-label={t('lighting.map.legendLabel')}>
      {(['low', 'medium', 'high', 'very_high'] as const).map((band) => (
        <li key={band} className="par-map__legend-item">
          <span className={`par-map__swatch par-map__swatch--${band}`} aria-hidden="true" />
          <span>{t(`lighting.bands.${band}`)}</span>
        </li>
      ))}
    </ul>
  );
}

export default ParMapPhoto;
